import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import intersection from 'lodash/intersection';
import config from 'config';

import {
  fetchShortCode,
  fetchShortCodeStatus,
  clearShortCode,
} from 'actions/auth';
import { useScalingRetryInterval } from 'hooks/retry';
import useInterval from 'hooks/interval';
import { useCurrentSubscriptions, useAuthN } from './subscriptions';
import { BasicAsyncStatus, VideoCtvRedux } from '../../store-types';
import { Provider } from 'app-types';

interface ShortCodeResult extends BasicAsyncStatus {
  shortCode?: string | null;
}

interface ShortCodeFlowResult extends ShortCodeResult {
  success: boolean;
}

export const useShortCode = (
  authTypes: Provider[] | undefined,
  onShortCode: (shortCode?: string) => void,
): ShortCodeResult => {
  const dispatch = useDispatch();

  const unmounted = useRef(false);
  useEffect(() => {
    return () => {
      unmounted.current = true;
    };
  }, []);
  const lastestOnShortCode = useRef(onShortCode);
  useEffect(() => {
    lastestOnShortCode.current = onShortCode;
  });

  const currentSubscriptions = useCurrentSubscriptions();
  useEffect(() => {
    const fetchCode = async () => {
      const shortCode = await dispatch(
        fetchShortCode(authTypes ?? [], currentSubscriptions),
      ) as unknown as string | undefined;
      if (!unmounted.current) lastestOnShortCode.current(shortCode);
    };

    if (intersection(authTypes, currentSubscriptions).length === 0) {
      fetchCode();
      return () => {
        dispatch(clearShortCode());
      };
    }

    return undefined;
  }, [dispatch, authTypes, currentSubscriptions]);

  const shortCode = useSelector(
    (state: VideoCtvRedux) => state.auth.shortCode.value,
  );
  const loading = useSelector(
    (state: VideoCtvRedux) => state.auth.shortCode.loading,
  );
  const error = useSelector(
    (state: VideoCtvRedux) => state.auth.shortCode.error,
  );
  return { shortCode, loading, error };
};

export const useShortCodeStatus = (shortCode: string | null = null): void => {
  const dispatch = useDispatch();
  const error = useSelector(
    (state: VideoCtvRedux) => state.auth.shortCodeStatus.error,
  );
  const baseInterval = config.api.shortCodeInterval;

  const { interval, resetInterval, onErrorChange } =
    useScalingRetryInterval(baseInterval);

  const resetRef = useRef<() => void>(() => {});
  const errorChangeRef = useRef<(error: unknown) => void>(() => {});
  useEffect(() => {
    resetRef.current = resetInterval;
    errorChangeRef.current = onErrorChange;
  });

  useInterval(async () => {
    if (shortCode) {
      const success = await (dispatch(fetchShortCodeStatus(shortCode)) as unknown as Promise<boolean>);
      if (success) resetRef.current();
    }
  }, interval);

  useEffect(() => {
    if (error) {
      errorChangeRef.current(error);
    }
  }, [error]);
};

const useShortCodeFlow = (
  authTypes: Provider[],
  onShortCode: (shortCode?: string) => void,
): ShortCodeFlowResult => {
  const { shortCode, loading, error } = useShortCode(authTypes, onShortCode);
  useShortCodeStatus(shortCode);

  const success = useAuthN(authTypes);

  return {
    shortCode,
    loading,
    error,
    success,
  };
};

export default useShortCodeFlow;
